import { Edit, ExternalLink } from 'lucide-react';

import { useTranslation } from '@/components/i18n/Translator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';

interface ConnectedAccount {
  provider: string;
  email: string;
  isConnected: boolean;
}

interface ProfileSectionProps {
  user: {
    name: string;
    email: string;
    avatar?: string;
  };
  connectedAccounts?: ConnectedAccount[];
  onEditProfile?: () => void;
}

const ProfileSection = ({
  user,
  connectedAccounts = [
    { provider: 'Google', email: user.email, isConnected: true }
  ],
  onEditProfile
}: ProfileSectionProps) => {
  const { t } = useTranslation();
  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">
            {t('profile.sections.information.title')}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onEditProfile}
            className="h-8"
          >
            <Edit className="w-4 h-4 mr-2" />
            {t('profile.sections.information.actions.editProfile')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Profile Info */}
          <div className="space-y-4">
            <div className="flex items-start space-x-4">
              <Avatar className="h-16 w-16">
                <AvatarImage src={user.avatar} alt="Profile" />
                <AvatarFallback className="text-lg font-medium bg-foreground text-background">
                  {user.name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1 flex-1">
                <h3 className="text-lg font-medium leading-none">
                  {user.name}
                </h3>
                <p className="text-sm text-muted-foreground">{user.email}</p>
                <div className="text-xs text-muted-foreground">
                  {t('profile.sections.information.memberSince', {
                    date: 'Nov 2024'
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Connected Accounts */}
          <div className="space-y-4">
            <div className="space-y-3">
              <h4 className="text-sm font-medium">
                {t('profile.sections.connectedAccounts.title')}
              </h4>
              {connectedAccounts.map((account, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-muted rounded flex items-center justify-center text-xs font-medium">
                      {account.provider.charAt(0)}
                    </div>
                    <div>
                      <div className="text-sm font-medium">
                        {account.provider}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {account.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge
                      variant={account.isConnected ? 'outline' : 'secondary'}
                      className={`text-xs ${
                        account.isConnected
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : ''
                      }`}
                    >
                      {account.isConnected
                        ? t(
                            'profile.sections.connectedAccounts.status.connected'
                          )
                        : t(
                            'profile.sections.connectedAccounts.status.disconnected'
                          )}
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                      <ExternalLink className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <Separator />

            <div className="space-y-2">
              <h4 className="text-sm font-medium">
                {t('profile.sections.security.title')}
              </h4>
              <div className="text-xs text-muted-foreground">
                {t('profile.sections.security.lastPasswordChange', {
                  timeAgo: '3 months ago'
                })}
              </div>
              <Button variant="outline" size="sm" className="h-7 text-xs">
                {t('profile.sections.security.actions.updatePassword')}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfileSection;
